import React, { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronRight, BarChart3, BookOpen, ClipboardList, FileSignature, FileText, FolderOpen, History, LayoutGrid, Link2, ListChecks, Palette, PiggyBank, Receipt, ScrollText, Send, ShieldCheck, SplitSquareHorizontal, User2, Users, Wallet, Building2, Gavel } from "lucide-react";
import { useCompactLayout } from "@/lib/compactLayout";
import ArrangeBoard from "@/components/nds/ArrangeBoard";

// Phones & tablets: a screen's section tabs become home-style colour cards; tapping one shows that tab as-is.
export const TONES = {
  people: { bar: "from-rose-500 to-pink-300", icon: "text-rose-400" },
  ops: { bar: "from-cyan-500 to-cyan-300", icon: "text-cyan-400" },
  data: { bar: "from-violet-500 to-fuchsia-400", icon: "text-violet-400" },
  financial: { bar: "from-emerald-500 to-emerald-300", icon: "text-emerald-400" },
  comms: { bar: "from-amber-500 to-yellow-300", icon: "text-amber-400" },
  content: { bar: "from-sky-500 to-indigo-300", icon: "text-sky-400" },
  system: { bar: "from-brand-blue to-brand-cyan", icon: "text-brand-cyan" },
};
const ICONS = {
  details: Receipt, lines: ListChecks, history: History, ledger: ScrollText, balances: Wallet, balance: PiggyBank, requests: Send,
  penalties: Gavel, paychecks: Wallet, split: SplitSquareHorizontal, outside_income: BarChart3, profile: User2, invoices: Receipt,
  escrow: Building2, reimbursables: Receipt, residents: Users, agreement: FileSignature, intake: ClipboardList, staff: ShieldCheck,
  mine: FileSignature, grid: LayoutGrid, library: FolderOpen, signed: FileText, all: Send, partners: Users, audit: History,
  link: Link2, forms: BookOpen, display: Palette,
};
const ROTATE = ["ops", "data", "financial", "comms", "people", "content", "system"];

function scrollBox(start) {
  for (let el = start; el; el = el.parentElement) if (el.scrollHeight > el.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
  return document.scrollingElement;
}

/** Tab state that starts on the card deck on phones/tablets. `deck` = show cards instead of a tab. */
export function useCardTabs(defaultTab, onPick) {
  const { compact } = useCompactLayout();
  const [picked, setPicked] = useState(compact ? null : defaultTab);
  const ref = useRef(null);
  const top = useRef(0);
  const deck = compact && !picked;
  const open = (id) => { if (deck) top.current = scrollBox(ref.current)?.scrollTop || 0; setPicked(id); onPick?.(id); };
  useLayoutEffect(() => { const el = scrollBox(ref.current); if (el && compact) el.scrollTop = deck ? top.current : 0; }, [deck]); // eslint-disable-line react-hooks/exhaustive-deps
  return { compact, deck, tab: picked || defaultTab, open, close: () => setPicked(null), ref };
}

/** items: [{ id, label, desc, icon, tone, stat, note, alert }] */
export function CardDeck({ items, onOpen, testid = "tab-cards" }) {
  const byId = Object.fromEntries(items.map((it, i) => [it.id, [it, i]]));
  return (
    <div data-testid={testid}>
    <ArrangeBoard board={testid.toLowerCase().replace(/[^a-z0-9_-]/g, "-")} ids={items.map((it) => it.id)} labels={Object.fromEntries(items.map((it) => [it.id, it.label]))} gridClass="grid-cols-1 md:grid-cols-2" testid={`${testid}-board`}
      renderTile={(id) => { const [it, i] = byId[id]; return (() => {
        const t = TONES[it.tone] || TONES[ROTATE[i % ROTATE.length]];
        const Icon = it.icon || ICONS[it.id] || ClipboardList;
        const live = it.stat != null || it.note;
        return (
          <button key={it.id} type="button" onClick={() => onOpen(it.id)} data-testid={`${testid}-${it.id}`}
            className="nds-cap-tile relative overflow-hidden bg-[rgba(17,26,46,0.75)] border border-white/[0.08] shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] text-left pt-4 pb-3 px-4 flex flex-col gap-2 active:scale-[0.99] transition-transform">
            <span className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${t.bar}`} />
            <div className="flex items-start justify-between gap-2">
              <div className={`w-8 h-8 grid place-items-center rounded-lg border border-slate-800 bg-navy-950 ${t.icon}`}><Icon className="w-3.5 h-3.5" strokeWidth={1.8} /></div>
              <ChevronRight className="h-4 w-4 text-slate-500" />
            </div>
            <div className="font-serif text-[0.95rem] font-bold text-slate-100 leading-tight">{it.label}</div>
            {it.desc && <p className="text-xs text-slate-400 leading-snug">{it.desc}</p>}
            {live && (
              <div data-testid={`${testid}-${it.id}-live`} className="border-t border-white/5 pt-2 flex items-baseline gap-2 min-w-0">
                {it.stat != null && <span className={`font-mono text-sm ${it.alert ? "text-amber-300" : "text-slate-100"} shrink-0`}>{it.stat}</span>}
                {it.note && <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 truncate">{it.note}</span>}
              </div>
            )}
          </button>
        );
      })(); }} />
    </div>
  );
}

export function CardsBack({ label, onClick, testid = "tab-cards-back", className = "mb-4" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`${className} inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold`}>
      <ArrowLeft className="h-3.5 w-3.5" /> All cards · <span className="text-nex-silver">{label}</span>
    </button>
  );
}
